import React from "react";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Hook simple para paginar arrays en cliente.
 * Útil cuando las páginas cargan TODA la data del backend pero queremos limitar
 * cuántos items se muestran por pantalla.
 *
 * Uso:
 *   const { paged, page, total, totalPages, next, prev, setPage } = usePagedList(filteredItems, 20);
 *   {paged.map(...)}
 *   <PaginationBar page={page} total={total} pageSize={20} onPrev={prev} onNext={next} />
 */
export function usePagedList(items, pageSize = 20) {
  const [page, setPage] = React.useState(0);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Reset page when items shrink (eg filter applied)
  React.useEffect(() => {
    if (page > 0 && page >= totalPages) setPage(0);
  }, [totalPages, page]);

  const start = page * pageSize;
  const paged = (items || []).slice(start, start + pageSize);

  return {
    paged,
    page,
    total,
    totalPages,
    pageSize,
    setPage,
    next: () => setPage((p) => Math.min(totalPages - 1, p + 1)),
    prev: () => setPage((p) => Math.max(0, p - 1)),
  };
}

export function PaginationBar({ page, total, pageSize, onPrev, onNext, label = "items" }) {
  if (total <= pageSize) return null;
  const start = page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div className="flex items-center justify-between gap-3 py-3 px-2 text-xs text-[#5c7368] border-t border-[#e6ece9]" data-testid="pagination-bar">
      <span>
        Mostrando <strong>{start}–{end}</strong> de <strong>{total}</strong> {label}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page === 0} onClick={onPrev} data-testid="pagination-prev">
          <ChevronLeft size={14} className="mr-1" /> Anterior
        </Button>
        <span className="tabular-nums">Página {page + 1} de {totalPages}</span>
        <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={onNext} data-testid="pagination-next">
          Siguiente <ChevronRight size={14} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}
